import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getDeviceOS } from '../utils/deviceInfo';
import { useUpdateProfile } from '../hooks/mutations/useUpdateProfile';
import { useQueryClient } from '@tanstack/react-query';

const MobileRedirectPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutate: updateProfile } = useUpdateProfile();
  const location = useLocation();
  const {goto} = location.state as {goto?: string} || {};
  
  const [countdown, setCountdown] = useState(5);
  const [isAppInstalled, setIsAppInstalled] = useState(true);
  const [hasTriedDeepLink, setHasTriedDeepLink] = useState(false);

  const deviceOS = getDeviceOS();

  useEffect(() => {
    if (deviceOS !== 'ios' && deviceOS !== 'android') {
      navigate('/', { replace: true });
      return;
    }

    setHasTriedDeepLink(true);
    
    // 앱 전환 감지를 위한 변수
    let appSwitched = false;
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        appSwitched = true;
      }
    };

    // 이벤트 리스너 등록
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // 딥링크 즉시 실행
    const deepLinkUrl = `nbbang://groups/${goto || 'main'}`;
    window.location.href = deepLinkUrl;

    // 카운트다운 시작
    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // 5초 후 앱 전환 여부 확인
    const timeoutId = setTimeout(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(countdownInterval);
      
      if (!appSwitched) {
        // 앱으로 전환되지 않았으면 앱이 설치되지 않은 것으로 판단
        setIsAppInstalled(false);
      
      } else {        
        window.close();
      }
    }, 5000);

    return () => {
      clearInterval(countdownInterval);
      clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [deviceOS, navigate, updateProfile, goto]);

  const handleAppStoreClick = () => {
    const storeUrl = deviceOS === 'ios' 
      ? 'https://apps.apple.com/kr/app/%EC%97%94%EB%B9%B5-%EC%A0%95%EC%82%B0-%EC%9D%B4%EC%A0%9C%EB%8A%94-%EC%9E%8A%EC%9C%BC%EC%84%B8%EC%9A%94/id6749234696' // 실제 앱스토어 URL로 변경 필요
      : 'https://play.google.com/store/apps/details?id=com.busitdol.nbbang&hl=ko';
    
    window.open(storeUrl, '_blank', 'noopener,noreferrer');
  };

  const handleContinueWeb = () => {
    // 설치 상태 업데이트
    const updateData = deviceOS === 'ios' 
    ? { ios_installed: false }
    : { android_installed: false };
    
    updateProfile(updateData);
    queryClient.setQueryData(['profile'], (prev: any) => {
      if (!prev) return prev; // 혹은 null → 기본값 넣기
      return {
        ...prev,
        ...updateData, // 특정 필드만 덮어씀
      };
    });
    if (goto) {
      navigate(`/groups/${goto}`, { replace: true });
    } else {
      navigate('/groups', { replace: true });
    }
  };

  // 앱이 설치되지 않은 상태에서 보여줄 UI
  if (!isAppInstalled && hasTriedDeepLink) {
    return (
      <div className="w-full min-h-[100svh] flex flex-col items-center justify-center px-4 bg-white">
        <div className="max-w-md w-full text-center">
          <div className="mb-8">
            <h1 className="text-xl font-bold text-gray-800 mb-4">
              모바일 앱에서는<br />
              터치 세 번만에<br />
              결제를 등록할 수 있어요
            </h1>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleAppStoreClick}
              className="w-full bg-blue-500 text-white font-semibold h-14 rounded-xl flex items-center justify-center"
            >
              {deviceOS === 'ios' ? '앱스토어에서 다운로드' : '플레이스토어에서 다운로드'}
            </button>
            
            <button
              onClick={handleContinueWeb}
              className="w-full bg-gray-100 text-gray-700 font-semibold h-14 rounded-xl flex items-center justify-center"
            >
              웹으로 계속 이용하기
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 앱 설치 확인 중 또는 딥링크 시도 중
  return (
    <div className="w-full min-h-[100svh] flex flex-col items-center justify-center px-4 bg-white">
      <div className="max-w-md w-full text-center">
        <div className="mb-8">
          <h1 className="text-xl font-bold text-gray-800 mb-4">
            모바일 앱이 설치된 것으로 보입니다!
          </h1>
          <p className="text-gray-600">
            모바일 앱으로 이동중... ({countdown}초)
          </p>
        </div>

        <div className="mt-8">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      </div>
    </div>
  );
};

export default MobileRedirectPage;